import PropTypes from 'prop-types'
import {useState, useCallback} from 'react'
import {Divider, Box, Text, Skeleton, Button} from '@chakra-ui/react'
import useSWR from 'swr'
import {useTranslation} from 'react-i18next'

import {
  fetcher,
  postHeaders,
  patchHeaders,
  deleteHeaders,
} from '../../../../api'
import {camelizeResult} from '../../../../api/serializer'
import CreateRecipientForm from './CreateRecipientForm'
import UpdateRecipientForm from './UpdateRecipientForm'
import InputFlexWrap from './InputFlexWrap'
import {
  SubsectionTitle,
  SubsectionContainer,
  SubsectionDescription,
} from '../../../components/Subsection'
import {onJsonResponse} from '../../../hooks/jsonResult'

export const PayerStatementRecipients = ({
  supplierId,
  customerId,
  customerEmail,
  authenticityToken,
}) => {
  const {t} = useTranslation('customers')

  const [showInput, setShowInput] = useState(false)

  const url = `/api/suppliers/${supplierId}/customers/${customerId}/payer_statement_recipients`

  const {data, mutate, isLoading} = useSWR(url, fetcher, {
    use: [camelizeResult],
  })

  const onCreate = useCallback(
    async (values, setFieldError) => {
      const result = await fetcher(
        url,
        postHeaders({
          authenticityToken,
          raiseClientErrors: false,
          body: values,
        }),
      )

      const onSuccess = (showMessage) => {
        setShowInput(false)
        mutate({recipients: [...data.recipients, values]})
        showMessage(t('customers:Statements.RecipientForm.toast.added'))
      }

      const onFailure = (key, error, value) =>
        setFieldError(
          key,
          t(`customers:Statements.RecipientForm.errors.${error}`, {value}),
        )

      onJsonResponse(result, {onSuccess, onFailure})
    },
    [t, url, data, mutate, authenticityToken],
  )

  const onUpdate = useCallback(
    async (recipient, values, setFieldError) => {
      const result = await fetcher(
        `${url}/${recipient.id}`,
        patchHeaders({
          authenticityToken,
          raiseClientErrors: false,
          body: values,
        }),
      )

      const onSuccess = (onToast) => {
        setShowInput(false)
        mutate()
        onToast(t('customers:Statements.RecipientForm.toast.updated'))
      }

      const onFailure = (key, error, value) =>
        setFieldError(
          key,
          t(`customers:Statements.RecipientForm.errors.${error}`, {value}),
        )

      onJsonResponse(result, {onSuccess, onFailure})
    },
    [t, url, mutate, authenticityToken],
  )

  const onRemove = useCallback(
    async (recipient) => {
      const result = await fetcher(
        `${url}/${recipient.id}`,
        deleteHeaders({authenticityToken, raiseClientErrors: false}),
      )

      const onSuccess = (onToast) => {
        mutate({
          recipients: data.recipients.filter(({id}) => id !== recipient.id),
        })
        onToast(t('customers:Statements.RecipientForm.toast.removed'))
      }

      onJsonResponse(result, {onSuccess})
    },
    [t, url, data, mutate, authenticityToken],
  )

  return (
    <SubsectionContainer data-testid="payer-statement-recipients" gap="xs">
      <SubsectionTitle title={t('customers:Statements.RecipientForm.title')} />
      <SubsectionDescription
        description={t('customers:Statements.RecipientForm.description')}
      />
      <Text className="fs-mask" fontWeight="medium">
        {customerEmail}
      </Text>
      <Divider />

      <Box pt="xs" className="fs-mask">
        {(showInput || data?.recipients.length > 0) && (
          <Text>Email address</Text>
        )}

        <Box data-testid="recipients">
          <Skeleton isLoaded={!isLoading}>
            {data?.recipients.map((recipient, index) => (
              <UpdateRecipientForm
                index={index}
                key={`${recipient.id}-${recipient.updatedAt}`}
                onSubmit={(values, {setFieldError}) =>
                  onUpdate(recipient, values, setFieldError)
                }
                email={recipient.email}
                onRemove={() => onRemove(recipient)}
                saveButtonText={t(
                  'customers:Statements.RecipientForm.buttons.save',
                )}
                savedButtonText={t(
                  'customers:Statements.RecipientForm.buttons.saved',
                )}
                removeButtonText={t(
                  'customers:Statements.RecipientForm.buttons.remove',
                )}
              />
            ))}
          </Skeleton>
        </Box>

        {showInput ? (
          <CreateRecipientForm
            onSubmit={(values, {setFieldError}) =>
              onCreate(values, setFieldError)
            }
            saveButtonText={t(
              'customers:Statements.RecipientForm.buttons.save',
            )}
          />
        ) : (
          <InputFlexWrap>
            {data?.recipients.length === 0 ? (
              <Button
                onClick={() => setShowInput(true)}
                variant="secondary"
                data-testid="add-additional-recipient-button"
              >
                {t('customers:Statements.RecipientForm.buttons.addAdditional')}
              </Button>
            ) : (
              <Button
                onClick={() => setShowInput(true)}
                variant="tertiary"
                data-testid="add-another-recipient-button"
              >
                {t('customers:Statements.RecipientForm.buttons.addAnother')}
              </Button>
            )}
          </InputFlexWrap>
        )}
      </Box>
    </SubsectionContainer>
  )
}

PayerStatementRecipients.propTypes = {
  supplierId: PropTypes.string.isRequired,
  customerId: PropTypes.string.isRequired,
  customerEmail: PropTypes.string.isRequired,
  authenticityToken: PropTypes.string.isRequired,
}

export default PayerStatementRecipients
